import { getThumbnailUrl, getOriginalUrl, getStrictThumbnailUrl } from '@/helpers/documentHelpers';
import { useDocumentUrl } from '@/hooks/useDocumentUrl';
import { Box, Icon } from '@palmetto/palmetto-components';
import { useCallback } from 'react';

import { ViewUrlType } from 'types';

import styles from './GalleryItem.module.scss';

export type GalleryItemProp = {
  item: any;
  index: number;
  useLightbox?: boolean;
  onClick?: (index: number) => void;
};

export const GalleryItem = ({ item: document, index, useLightbox = false, onClick }: GalleryItemProp) => {
  const viewUrls = document.files[0].viewUrls;
  const thumbnailUrl = getThumbnailUrl(viewUrls);
  const originalViewUrl = getOriginalUrl(viewUrls);
  const fileName = document.files[0].originalName;
  const isImage = document.files[0].contentType.includes('image');
  const { handleImageError } = useDocumentUrl();
  const accountId = document.accountId?.toString() ?? '';
  const documentId = document.id?.toString() ?? '';

  const handleClick = useCallback(
    (event: any) => {
      if (useLightbox) {
        event.preventDefault();
      }
      if (onClick) {
        onClick(index);
      }
    },
    [useLightbox, onClick, index],
  );

  const onImageError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      handleImageError(event, accountId, documentId, ViewUrlType.thumbnail);
    },
    [handleImageError, accountId, documentId],
  );

  return (
    <Box
      as="a"
      title={fileName}
      radius="sm"
      overflow="hidden"
      padding="2xs"
      background="tertiary"
      hover={{ background: 'grey-200' }}
      cursor="pointer"
      target="_blank"
      onClick={handleClick}
    >
      <Box
        className={styles.imageContainer}
        href={originalViewUrl}
        data-grid-inline-offset="true"
        height={{ base: 'md', tablet: 'lg' }}
      >
        {isImage || getStrictThumbnailUrl(viewUrls) ? (
          <img
            className={styles.image}
            src={thumbnailUrl ?? originalViewUrl}
            alt={fileName}
            height="100%"
            loading="lazy"
            onError={onImageError}
          />
        ) : (
          <Icon name="document" size="3xl" style={{ margin: 'auto' }} />
        )}
      </Box>
      <Box padding="xs" fontSize="xs" background="primary" childGap="2xs">
        <span className={styles.documentFileName}>{fileName}</span>
      </Box>
    </Box>
  );
};
